import { useState, useEffect } from "react";
import {
  IconPencil,
  IconPlus,
  IconSpinner,
  IconTrash,
} from "../icons";
import { notifyError, notifySuccess } from "../notify";
import { useAdminModal } from "../components/AdminModalProvider";
import AdminModal from "../components/AdminModal";

export default function CategoryManagement() {
  const { confirm } = useAdminModal();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "" });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch("/api/categories", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.details ||
              errorData.error ||
              "Failed to fetch categories"
          );
        }
        throw new Error("Server error. Please try again later.");
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      notifyError(
        error.message === "Failed to fetch"
          ? "Network error. Please check your connection."
          : error.message
      );
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const openModal = (category = null) => {
    if (category) {
      setFormData({ name: category.name });
      setEditingCategory(category);
    } else {
      setFormData({ name: "" });
      setEditingCategory(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (actionLoading) return;
    setIsModalOpen(false);
    setFormData({ name: "" });
    setEditingCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name.trim();
    if (!name) {
      notifyError("Category name is required");
      return;
    }

    setActionLoading(true);
    try {
      const url = editingCategory
        ? `/api/categories/${editingCategory.id}`
        : "/api/categories";

      const method = editingCategory ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.details ||
              errorData.error ||
              "Failed to save category"
          );
        }
        throw new Error("Server error. Please try again later.");
      }

      await fetchCategories();
      closeModal();
      notifySuccess(
        `Category successfully ${editingCategory ? "updated" : "created"}`
      );
    } catch (error) {
      notifyError(
        error.message === "Failed to fetch"
          ? "Network error. Please check your connection."
          : error.message
      );
      console.error("Error saving category:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await confirm({
      title: "Delete category?",
      description: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "danger",
    });

    if (!confirmed) return;

    setActionLoading(true);
    try {
      const response = await fetch(`/api/categories/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.details ||
              errorData.error ||
              "Failed to delete category"
          );
        }
        throw new Error("Server error. Please try again later.");
      }

      await fetchCategories();
      notifySuccess(`Category "${name}" was successfully deleted.`);
    } catch (error) {
      notifyError(
        error.message === "Failed to fetch"
          ? "Network error. Please check your connection."
          : error.message
      );
      console.error("Error deleting category:", error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <IconSpinner size={32} />
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="category-management">
      <div className="category-management__toolbar">
        <button
          type="button"
          className="category-management__add"
          onClick={() => openModal()}
          disabled={actionLoading}
        >
          <IconPlus size={16} />
          Add New
        </button>
      </div>

      <div className="management-grid">
        {categories.length === 0 ? (
          <div className="no-data-message">
            No categories found. Click Add New to create one.
          </div>
        ) : (
          categories.map((category) => (
            <div key={category.id} className="management-card">
              <div className="card-content">
                <h3>{category.name}</h3>
                <p className="date-info">
                  Created: {new Date(category.createdAt).toLocaleDateString()}
                  <br />
                  Updated: {new Date(category.updatedAt).toLocaleDateString()}
                </p>
              </div>
              <div className="card-actions">
                <button
                  type="button"
                  className="edit-btn"
                  onClick={() => openModal(category)}
                  disabled={actionLoading}
                  title="Edit category"
                >
                  <IconPencil size={18} />
                </button>
                <button
                  type="button"
                  className="delete-btn"
                  onClick={() => handleDelete(category.id, category.name)}
                  disabled={actionLoading}
                  title="Delete category"
                >
                  <IconTrash size={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <AdminModal
        open={isModalOpen}
        onClose={closeModal}
        closeOnOverlay={!actionLoading}
        showClose={!actionLoading}
        title={editingCategory ? "Edit category" : "Add category"}
        description={
          editingCategory
            ? "Update the category name below."
            : "Create a new category for scheme features."
        }
        size="md"
        footer={
          <>
            <button
              type="button"
              className="admin-modal__btn admin-modal__btn--secondary"
              onClick={closeModal}
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="category-form"
              className="admin-modal__btn admin-modal__btn--primary"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <IconSpinner size={16} />
                  {editingCategory ? "Updating…" : "Creating…"}
                </>
              ) : editingCategory ? (
                "Update"
              ) : (
                "Create"
              )}
            </button>
          </>
        }
      >
        <form id="category-form" onSubmit={handleSubmit}>
          <div className="admin-modal__field">
            <label htmlFor="category-name">Category name</label>
            <input
              type="text"
              id="category-name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={actionLoading}
              placeholder="Enter category name"
              autoFocus
            />
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
